/**
 * 数据导出（阶段 7 / §28）。
 *
 * - 本地导出：JSON 完整备份 + 四类 CSV（训练组 / 饮食 / 身体 / 进阶决策），
 *   导出前要求**明确确认**（ADR-011 的离线路径），并标注「仅包含本设备当前数据」；
 *   内容含规则版本与单位口径；CSV 序列化复用 @jilian/core（单测覆盖转义）。
 * - 云端导出：配置 Supabase 且登录后，要求**重新输入密码**换取新 JWT（ADR-011 重新验证），
 *   调 export-data Edge Function；未配置时如实说明不可用。
 */

import { db } from "../db/db";
import { sectionsToCsv, type CsvRecord } from "@jilian/core";
import { RULES_VERSION, EXERCISE_LIBRARY_VERSION, SCHEMA_VERSION } from "@jilian/shared";
import { AUTH_PROVIDER } from "./sync/syncClient";

function download(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function fileStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

// ---------------------------------------------------------------- 本地导出

/** JSON 完整备份（本设备数据；不含未配置的云端数据）。 */
export async function exportLocalJson(): Promise<void> {
  const [exercises, sessions, exerciseItems, sets, meals, measurements, intents, decisions] = await Promise.all([
    db.exercises.toArray(),
    db.sessions.toArray(),
    db.exerciseItems.toArray(),
    db.sets.toArray(),
    db.meals.toArray(),
    db.measurements.toArray(),
    db.dailyIntent.toArray(),
    db.progressionDecisions.toArray(),
  ]);
  const payload = {
    exportedAt: new Date().toISOString(),
    scope: "local_device_only" as const,
    scopeNotice: "仅包含本设备当前数据（未同步/未上传的部分以 sync_events 状态为准）。",
    schemaVersion: SCHEMA_VERSION,
    rulesVersion: RULES_VERSION,
    exerciseLibraryVersion: EXERCISE_LIBRARY_VERSION,
    weightUnitScopeNotice: "重量口径随各字段 weightUnitScope 保存（total/per_dumbbell/per_side/machine_display/bodyweight/bodyweight_plus/assistance），不做换算。",
    exercises,
    sessions,
    exerciseItems,
    sets,
    meals,
    measurements,
    dailyIntents: intents,
    progressionDecisions: decisions,
  };
  download(`jilian-local-export-${fileStamp()}.json`, JSON.stringify(payload, null, 2), "application/json");
}

/** 训练组 CSV（每行含动作名/归属日/重量/次数/RIR/口径/不适标记）。 */
export async function exportSetsCsv(): Promise<void> {
  const [sessions, exerciseItems, sets, exercises] = await Promise.all([
    db.sessions.toArray(),
    db.exerciseItems.toArray(),
    db.sets.toArray(),
    db.exercises.toArray(),
  ]);
  const sessionById = new Map(sessions.map((s) => [s.id, s]));
  const itemById = new Map(exerciseItems.map((i) => [i.id, i]));
  const nameById = new Map(exercises.map((e) => [e.id, e.nameZh]));

  const rows: CsvRecord[] = sets
    .map((s) => {
      const item = itemById.get(s.workoutExerciseId);
      const session = item ? sessionById.get(item.sessionId) : undefined;
      return {
        动作: item ? (nameById.get(item.exerciseId) ?? item.exerciseId) : s.id,
        归属日: session?.localDateAtStart ?? "",
        组序号: s.setIndex,
        实际重量kg: s.actualWeightKg ?? "",
        次数: s.reps ?? "",
        RIR: s.rir ?? "",
        口径: s.weightUnitScope,
        力竭: s.reachedFailure ? "是" : "否",
        关节疼痛: s.jointPain ? "是" : "否",
        伤处不适: s.injuryPain ? "是" : "否",
        状态: s.status,
        完成时间: s.completedAt ?? "",
      };
    })
    .sort((a, b) => String(a.归属日).localeCompare(String(b.归属日)) || Number(a.组序号) - Number(b.组序号));
  const headers = ["动作", "归属日", "组序号", "实际重量kg", "次数", "RIR", "口径", "力竭", "关节疼痛", "伤处不适", "状态", "完成时间"];
  download(`jilian-sets-${fileStamp()}.csv`, sectionsToCsv([{ title: "workout_sets", headers, rows }]), "text/csv;charset=utf-8");
}

/** 饮食 CSV（原文 / 状态 / 修正 / 标定热量宏量）。 */
export async function exportMealsCsv(): Promise<void> {
  const meals = await db.meals.toArray();
  const rows: CsvRecord[] = meals
    .map((m) => ({
      餐次: m.mealType,
      日期: m.consumedAt.slice(0, 10),
      原文: m.rawText,
      状态: m.processingStatus,
      热量kcal: m.finalCalories ?? "",
      蛋白g: m.finalProtein ?? "",
      脂肪g: m.finalFat ?? "",
      碳水g: m.finalCarbs ?? "",
      用户修正: m.userCorrected ? "是" : "否",
    }))
    .sort((a, b) => String(a.日期).localeCompare(String(b.日期)));
  const headers = ["餐次", "日期", "原文", "状态", "热量kcal", "蛋白g", "脂肪g", "碳水g", "用户修正"];
  download(`jilian-meals-${fileStamp()}.csv`, sectionsToCsv([{ title: "meal_entries", headers, rows }]), "text/csv;charset=utf-8");
}

/** 身体数据 CSV。 */
export async function exportMeasurementsCsv(): Promise<void> {
  const measurements = await db.measurements.toArray();
  const rows: CsvRecord[] = measurements.map((m) => ({
    日期: m.date,
    体重kg: m.weightKg,
    腰围cm: m.waistCm ?? "",
    体脂: m.bodyFatPercent ?? "",
    场景: m.measurementContext,
  }));
  const headers = ["日期", "体重kg", "腰围cm", "体脂", "场景"];
  download(`jilian-body-${fileStamp()}.csv`, sectionsToCsv([{ title: "body_measurements", headers, rows }]), "text/csv;charset=utf-8");
}

/** 进阶决策 CSV（加重/维持/降重/试重 + 依据 + 规则版本 + 用户覆盖）。 */
export async function exportDecisionsCsv(): Promise<void> {
  const decisions = await db.progressionDecisions.toArray();
  const rows: CsvRecord[] = decisions.map((d) => ({
    动作id: d.exerciseId,
    决策: d.decision,
    原重量kg: d.previousWeightKg ?? "",
    建议重量kg: d.suggestedWeightKg ?? "",
    口径: d.weightUnitScope,
    依据码: d.reasonCodes.join("|"),
    依据文本: d.humanReadableReason,
    规则版本: d.ruleVersion,
    用户采纳: d.userAccepted ? "是" : "否",
    用户覆盖重量kg: d.userOverrideWeightKg ?? "",
    日期: d.createdAt.slice(0, 10),
  })).sort((a, b) => String(a.日期).localeCompare(String(b.日期)));
  const headers = ["动作id", "决策", "原重量kg", "建议重量kg", "口径", "依据码", "依据文本", "规则版本", "用户采纳", "用户覆盖重量kg", "日期"];
  download(`jilian-decisions-${fileStamp()}.csv`, sectionsToCsv([{ title: "progression_decisions", headers, rows }]), "text/csv;charset=utf-8");
}

// ---------------------------------------------------------------- 云端导出（ADR-011 重新验证）

export interface CloudExportCheck {
  available: boolean;
  reason: string | null;
}

export async function cloudExportAvailable(): Promise<CloudExportCheck> {
  if (AUTH_PROVIDER.kind === "local") {
    return { available: false, reason: "未配置云端账户（VITE_SUPABASE_URL），仅支持本机导出（标注「仅包含本设备当前数据」）。" };
  }
  const session = await AUTH_PROVIDER.getSession();
  if (!session) {
    return { available: false, reason: "尚未登录：登录后可使用「重新输入密码 → 云端导出」。" };
  }
  return { available: true, reason: null };
}

/**
 * 云端导出：重新输入密码（ADR-011 重新验证）→ 用新 JWT 调 export-data。
 * 返回结果对象（下载由本函数执行）；密码不落库、不加 VITE 前缀、不出客户端包之外。
 */
export async function exportCloud(password: string, format: "json" | "csv"): Promise<{ ok: boolean; message: string }> {
  const provider = AUTH_PROVIDER;
  if (provider.kind !== "supabase") return { ok: false, message: "云端导出不可用（未配置账户服务）。" };
  const session = await provider.getSession();
  if (!session) return { ok: false, message: "请先登录。" };
  const supabase = (provider as unknown as { getClient: () => import("@supabase/supabase-js").SupabaseClient }).getClient();

  try {
    // 重新验证：用密码再登录一次换取新 JWT（ADR-011 重新验证；不覆盖主会话状态）
    const { data: reauth } = await supabase.auth.signInWithPassword({
      email: session.user.email ?? "",
      password,
    });
    const reauthToken = reauth.session?.access_token;
    if (!reauthToken) return { ok: false, message: "重新验证失败：密码不正确。" };

    // 文件流下载：不走 functions.invoke（其只解析 JSON），直接 fetch 并落盘
    const base = (import.meta.env.VITE_SUPABASE_URL as string).replace(/\/$/, "");
    const res = await fetch(`${base}/functions/v1/export-data`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ format, reauthToken }),
    });
    if (!res.ok) {
      let msg = `导出失败（HTTP ${res.status}）`;
      try {
        const body = (await res.json()) as { error?: string };
        if (body.error) msg = body.error;
      } catch {
        /* 保留默认信息 */
      }
      return { ok: false, message: msg.slice(0, 300) };
    }
    const blob = await res.blob();
    const disposition = res.headers.get("Content-Disposition") ?? "";
    const match = disposition.match(/filename="?([^";]+)"?/);
    const filename = match ? match[1]! : `jilian-cloud-export-${fileStamp()}.${format}`;
    download(filename, await blob.text(), format === "json" ? "application/json" : "text/csv;charset=utf-8");
    return { ok: true, message: `云端导出已下载：${filename}（含规则版本与单位口径说明）。` };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : "导出失败（网络或服务端不可用）" };
  }
}